import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCatalogoApi } from '../catalogo/api'
import { t, Lang } from './i18n'
import { t as tAsistencia } from '../asistencia/i18n'
import { estadoLabel, EstadoF11 } from '../asistencia/f11/i18n'
import { EmptyState } from '../styles/EmptyState'
import { Spinner } from '../styles/Spinner'
import { formatDate, localeFor } from '../utils/format'
import styles from './PanelListas.module.css'

export interface AusenciaJugador {
  miembro_equipo_id: string
  nombre: string
  alias: string | null
  equipo: { id: string; nombre: string; categoria: string }
  ultima_asistencia: string | null
  dias_sin_asistir: number
  faltas_seguidas: number
  motivo: string | null
  nota: string | null
}

const MOTIVOS_ESKOLA_F7 = ['EN', 'LS', 'JU', 'NJ'] as const

function motivoLabel(categoria: string, motivo: string | null, lang: Lang): string {
  if (!motivo) return t('motivoSinMarcar', lang)
  if (categoria === 'f11') return estadoLabel(motivo as EstadoF11, lang)
  if ((MOTIVOS_ESKOLA_F7 as readonly string[]).includes(motivo)) {
    return tAsistencia(`falta${motivo as (typeof MOTIVOS_ESKOLA_F7)[number]}`, lang)
  }
  return t('motivoAusente', lang)
}

// Issue #85 (club, 2026-10-23): "En Ausencias aparece cada jugador que lleva más de una semana sin
// asistir, con su nombre, su equipo, el motivo registrado de la ausencia y cuánto tiempo lleva sin
// entrenar". Datos de GET /panel/ausencias (criterio en el API), los que más tiempo llevan primero.
export function PanelAusenciasTab({ temporadaId, lang }: { temporadaId: string; lang: Lang }) {
  const api = useCatalogoApi()
  const [ausencias, setAusencias] = useState<AusenciaJugador[] | null>(null)
  const locale = localeFor(lang)

  useEffect(() => {
    setAusencias(null)
    api.get(`/panel/ausencias?temporada_id=${temporadaId}`).then(setAusencias)
  }, [temporadaId])

  if (ausencias === null) return <Spinner />

  return (
    <div className={styles.contenedor}>
      <p className={styles.ayuda}>{t('ausenciasAyuda', lang)}</p>
      {ausencias.length === 0 ? (
        <EmptyState text={t('sinAusencias', lang)} icon="✅" />
      ) : (
        <div className="table-scroll">
          <table className="sticky-first-col" aria-label={t('vistaAusencias', lang)}>
            <thead>
              <tr>
                <th>{t('jugador', lang)}</th>
                <th>{t('equipo', lang)}</th>
                <th>{t('motivo', lang)}</th>
                <th>{t('sinEntrenar', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {ausencias.map((a) => (
                <tr key={a.miembro_equipo_id}>
                  <td>{a.nombre}</td>
                  <td>
                    <Link to={`/equipos/${a.equipo.id}`} className={styles.equipo} data-categoria={a.equipo.categoria}>
                      {a.equipo.nombre}
                    </Link>
                  </td>
                  <td>
                    <span className={styles.motivo}>{motivoLabel(a.equipo.categoria, a.motivo, lang)}</span>
                    {a.nota && <small className={styles.detalle}>«{a.nota}»</small>}
                  </td>
                  <td>
                    <strong className={a.dias_sin_asistir > 14 ? styles.grave : styles.aviso}>
                      {t('diasN', lang).replace('{n}', String(a.dias_sin_asistir))}
                    </strong>
                    <small className={styles.detalle}>
                      {a.ultima_asistencia
                        ? t('ultimaAsistencia', lang).replace('{fecha}', formatDate(a.ultima_asistencia, locale))
                        : t('nuncaEnBloque', lang)}
                      {' · '}
                      {t('faltasSeguidas', lang).replace('{n}', String(a.faltas_seguidas))}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
